import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { reloadPage } from '@/utils/reloadPage';

const RELOAD_MARKER = 'route-error:last-reload-at';
const RELOAD_WINDOW_MS = 10_000;

/**
 * A failed dynamic import surfaces here as a thrown error rather than through
 * lazyWithRetry when it happens below the lazy boundary (a nested import, or a
 * dependency chunk that 404s / returns Vite's dev "504 Outdated Optimize Dep").
 * Detect those by message so we can self-heal with a reload instead of stranding
 * the user on the error page.
 */
function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /dynamically imported module|module script failed|Loading chunk|Outdated Optimize Dep|Failed to fetch/i.test(
    message
  );
}

/**
 * Router-level `errorElement`: catches anything thrown while matching or
 * rendering a route — including the 404 the router raises for an unmatched
 * URL — and renders a branded page instead of React Router's default
 * developer error screen. A genuine 404 reuses the standard not-found copy;
 * a stale-chunk error triggers a one-time reload; anything else shows a
 * generic recoverable message.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();

  const chunkError = !isRouteErrorResponse(error) && isChunkLoadError(error);

  useEffect(() => {
    if (!chunkError) return;

    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_MARKER) ?? '0');
    if (Date.now() - lastReloadAt > RELOAD_WINDOW_MS) {
      window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
      reloadPage();
    }
  }, [chunkError]);

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  const code = isRouteErrorResponse(error) ? String(error.status) : 'Error';

  return <NotFoundPage code={code} title={t('routeError.title')} message={t('routeError.message')} />;
}
